import { Injectable } from '@angular/core';
import { captureException } from '@sentry/angular-ivy';
import { GoogleEventTypes } from '../../enum/datalayer-event-types';
import { GoogleTagManagerService } from '../google-tag-manager.service';
import {
  SelectItemEvent,
  DefaultEcommerceEvent,
  PaymentInfoEvent,
  PurchaseEvent,
  ShippingInfoEvent,
} from '../../models/datalayer.model';
@Injectable({
  providedIn: 'root',
})
export class DataLayerService {
  constructor(private googleTagService: GoogleTagManagerService) {}

  public pushGoogleEvent(eventName: GoogleEventTypes, data: any) {
    try {
      this.pushToDataLayer(eventName, data);
    } catch (error) {
      console.error(error);
      captureException(error);
    }
  }

  private pushToDataLayer(eventName: GoogleEventTypes, data: any) {
    const serializeFunction = this.eventMapper[eventName];
    if (!serializeFunction) {
      throw new Error(
        `No serialization function found for event: ${eventName}`
      );
    }

    const serializedData = serializeFunction(data);
    if (serializedData?.items && serializedData?.items.length > 0) {
      serializedData.items[0] = {
        ...serializedData?.items[0],
        ...this.categoryProps(data.categories),
      };
    }
    this.checkForUndefinedProperties(eventName, serializedData);
    this.googleTagService.sendGoogleEvent(eventName, serializedData);
  }

  private eventMapper: { [eventName: string]: (data: any) => any } = {
    select_item: this.selectItemSerializer,
    view_item: this.viewItemSerializer,
    add_to_cart: this.cartSerializer,
    begin_checkout: this.cartSerializer,
    add_shipping_info: this.shippingSerializer,
    add_payment_info: this.paymentInfoSerializer,
    purchase: this.purchaseSerializer,
  };

  private checkForUndefinedProperties(eventName: string, data: any) {
    const items = data.items;
    this.checkUndefinedProp(eventName, data);
    items.forEach((item) => this.checkUndefinedProp(eventName, item));
  }

  private checkUndefinedProp(eventName: string, data: Object) {
    for (const key in data) {
      if (
        data.hasOwnProperty(key) &&
        (data[key] === undefined ||
          data[key].toString() === 'NaN' ||
          data[key] === '')
      ) {
        const errorMsg = `[ GOOGLE TAGS EVENT ] : invalid property: ${key} on ${eventName}`;
        captureException(new Error(errorMsg));
        throw new Error(errorMsg);
      }
    }
  }

  private selectItemSerializer(data: any): SelectItemEvent {
    return {
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          index: parseFloat(data.index),
          discount: parseFloat(data.store_product_detail.discount),
        },
      ],
    };
  }

  private viewItemSerializer(data: any): DefaultEcommerceEvent {
    return {
      currency: data.currency_detail.code,
      value: parseFloat(
        data.store_product_detail.store_varieties[0].variety_detail.min_price
      ),
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          discount: parseFloat(data.store_product_detail.discount),
        },
      ],
    };
  }

  private cartSerializer(data: any): DefaultEcommerceEvent {
    return {
      currency: data.currency,
      value: parseFloat(data.payablePrice),
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          discount: parseFloat(data.store_product_detail.discount),
          price: parseFloat(data.price),
          quantity: data.quantity,
        },
      ],
    };
  }

  private paymentInfoSerializer(data: any): PaymentInfoEvent {
    return {
      currency: data.currency,
      value: parseFloat(data.payablePrice),
      payment_type: data.paymentType,
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          discount: parseFloat(data.discount),
          price: parseFloat(data.price),
          quantity: data.quantity,
        },
      ],
    };
  }

  private shippingSerializer(data: any): ShippingInfoEvent {
    return {
      currency: data.currency,
      value: +data.payablePrice,
      shipping_tier: 'digital',
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          discount: parseFloat(data.discount),
          price: parseFloat(data.price),
          quantity: data.quantity,
        },
      ],
    };
  }

  private purchaseSerializer(data: any): PurchaseEvent {
    return {
      currency: data.currency,
      value: parseFloat(data.payablePrice),
      transaction_id: data.transactionId,
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          discount: parseFloat(data.discount),
          price: parseFloat(data.price),
          quantity: data.quantity,
        },
      ],
    };
  }

  private categoryProps(categoryList: any) {
    if (!categoryList || categoryList.length === 0) return;
    let categories = {};
    categoryList.forEach(
      (el, i) => (categories[`item_category${i + 1}`] = el.name)
    );
    return categories;
  }
}
